import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import './AppShell.css';

const NAV = [
  { to: '/review/piscataway', label: 'Example review' },
  { to: '/checks', label: 'What it checks' },
  { to: '/method', label: 'How it works' },
  { to: '/new', label: 'Start a review' },
];

/**
 * The workspace is a working session, not a page in a site. IA.md: the
 * navigation collapses there to the wordmark, the document, the decided counter
 * and the way to the comment sheet — all of which the workspace draws itself.
 * The shell gets out of its way rather than stacking a second header above it.
 */
function isWorkingSession(pathname: string): boolean {
  return pathname.startsWith('/review/');
}

export default function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();

  if (isWorkingSession(pathname)) {
    return <div className="shell shell--bare">{children}</div>;
  }

  return (
    <div className="shell">
      <header className="shell__header">
        <div className="shell__header-inner">
          <NavLink to="/" className="shell__lockup">
            Ceiling
          </NavLink>
          <span className="shell__spacer" />
          <div className="shell__nav">
            {NAV.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  isActive
                    ? 'shell__navlink shell__navlink--active'
                    : 'shell__navlink'
                }
              >
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      </header>

      <main className="shell__main">{children}</main>

      <footer className="shell__footer">
        <div className="shell__footer-inner">
          <p className="shell__footer-line">
            Ceiling quotes the clause and cites the federal audit that records
            the same cause. It does not rate severity, score contracts, or
            propose contract language.
          </p>
          {/* The guide is not in the top nav — IA.md keeps that to four
              items — so this is where someone who has read the page and is
              still unsure finds it. */}
          <NavLink to="/guide" className="shell__footer-link">
            New here? Read the two-minute guide &rarr;
          </NavLink>
        </div>
      </footer>
    </div>
  );
}
